import React, { useState } from 'react';
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import UserAvatar from 'app/components/UserAvatar';
import renew from "../../../../../public/images/product-renew.svg";
import { DateTime } from 'luxon';
import StatusIndicator from '../../StatusIndicator';
import { schedulingFetchDelete } from 'app/utils/request/scheduling/fetch';
import { toast } from 'react-toastify';
import OfferingActionModal from '../../OfferingActionModal';
import PaymentActionsDropdown from '../../Customers/CustomerDetails/PaymentActionsDropdown';
import dropdownStyles from '../../Customers/CustomerDetails/styles.module.scss';

interface AthleteListItemProps {
  athlete: any;
  productId: string;
  coachId: string;
  fetchHandler: () => void;
  product: any;
  onMarkAsPaid: (purchaseId: number) => void;
  onMarkAsUnpaid: (purchaseId: number) => void;
  markingPaidId: number | null;
}

export default function AthleteListItem({ athlete, productId, coachId, fetchHandler, product, onMarkAsPaid, onMarkAsUnpaid, markingPaidId }: AthleteListItemProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const handleCancel = () => {
    setShowModal(false);
  };

  const handleConfirm = async () => {
    setLoading(true);
    setShowModal(false);
    const productData = {
      coach_id: coachId,
      stripe_product_id: productId,
      student_profile_id: athlete.studentProfileId,
    };

    schedulingFetchDelete(`/stripe_purchase`, productData)
      .then((_) => {
        toast.success(t("Subscription canceled"));
        fetchHandler();
      })
      .catch((_) => {
        toast.error(t("An unexpected error has occurred."));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const formattedDate = athlete?.renewalDate
  ? DateTime.fromISO(athlete.renewalDate).toFormat('MMM d')
  : t("N/A");

  // CN-2674: a cycle that never settled is still a live Stripe subscription — it
  // just never got paid — so it needs the same Cancel action. Without it, a
  // subscription whose FIRST invoice went unpaid kept billing every month with
  // nothing the coach could do about it.
  const isUnsettled = ['past_due', 'failed'].includes(athlete.lastPurchaseStatus);

  // CN-2832: a scheduled start can be cancelled before it charges anything.
  const isScheduled = athlete.lastPurchaseStatus === 'scheduled';

  const canCancel =
    isUnsettled ||
    isScheduled ||
    athlete.lastPurchaseStatus === 'paid' ||
    athlete.lastPurchaseStatus === 'pending';

  const canMarkAsPaid =
    athlete.lastPurchaseId &&
    athlete.lastPurchaseType === 'offline' &&
    ['pending', 'past_due'].includes(athlete.lastPurchaseStatus);

  const canMarkAsUnpaid =
    athlete.lastPurchaseId &&
    athlete.lastPurchaseType === 'offline' &&
    athlete.lastPurchaseStatus === 'paid';

  const hasActions = canCancel || canMarkAsPaid || canMarkAsUnpaid;

  const modalContent = (
    <div>
      {t("Are you sure you want to cancel the athlete's")}{" "}
      <strong style={{ color: "grey" }}>{product?.title}</strong>{" "}
      {t("subscription?")}
      <br />
      {isScheduled ? (
        // CN-2832: renewalDate is the start date while scheduled.
        t("It hasn't started yet, so they won't be charged on {{date}} or after.", { date: formattedDate })
      ) : (
        t("If you cancel, they won't be charged at the next billing cycle.")
      )}
      <br />
      {isScheduled ? null : isUnsettled ? (
        t(
          "This subscription has an unpaid cycle. Cancelling stops future billing, but an invoice already open in Stripe stays open until you void it there.",
        )
      ) : (
        <>
          {t("It will remain valid until")}{" "}
          <strong style={{ color: "grey" }}>{formattedDate}</strong>.
        </>
      )}
      <br />
    </div>
  );

  return (
    <div className={styles.athlete_list_item}>
      <div className={styles.name_container}>
        <div className={styles.avatar}>
          <UserAvatar
              srcName={athlete.profilePicture}
              userName={athlete.firsName + " " + athlete.lastName}
              altName="avatar"
              className={styles.avatar}
              sizeHeight={40}
              sizeWidth={40}
            />
        </div>
        <div>{athlete.firstName} {athlete.lastName}</div>
      </div>
        <div className={styles.method}>{athlete.paymentMethod || '-'}</div>
        <div className={styles.collection}>{athlete.collectionType || '-'}</div>
        {/* CN-2641: show "cycle X of N" when the offering caps billing cycles */}
        <div className={styles.cycles}>
          {product?.maxBillingCycles
            ? `${athlete.purchaseCount}/${product.maxBillingCycles}`
            : athlete.purchaseCount}
        </div>
        <StatusIndicator payment_status={athlete.lastPurchaseStatus} purchase_type={athlete.lastPurchaseType}/>
        <div className={styles.date}>
          {athlete.renewalDate ? (
            <>
              <div>{athlete.renewalDate !== 'N/A' ? DateTime.fromISO(athlete.renewalDate).toFormat('MMM d, yyyy') : '-'}</div>
              {/* CN-2832: before a custom start, this date is the first charge. */}
              {isScheduled ? <div className={styles.invoice}>{t('First charge')}</div> :
              athlete.subscriptionType === 'invoice'? <div className={styles.invoice}>{t('Invoice')}</div> :
              (
                <div className={styles.renewal}>
                  <img src={renew} alt="close"/>
                    <div>{t('Auto-renew')}</div>
                </div>
              )}
            </>
          ) : '-'}
        </div>
        <div className={styles.actions_column}>
          {hasActions ? (
            <div className={`${dropdownStyles.actionWrapper} ${(loading || markingPaidId === athlete.lastPurchaseId) ? dropdownStyles.actionDisabled : ''}`}>
              <PaymentActionsDropdown disabled={loading || markingPaidId === athlete.lastPurchaseId}>
                <div className={dropdownStyles.dropdownContent}>
                  {canMarkAsPaid && (
                    <button
                      onClick={() => onMarkAsPaid(athlete.lastPurchaseId)}
                      className={dropdownStyles.dropdownItem}
                      disabled={markingPaidId === athlete.lastPurchaseId}
                    >
                      <i className="icon-ok" />
                      <span>{t('Mark as Paid')}</span>
                    </button>
                  )}
                  {canMarkAsUnpaid && (
                    <button
                      onClick={() => onMarkAsUnpaid(athlete.lastPurchaseId)}
                      className={dropdownStyles.dropdownItem}
                      disabled={markingPaidId === athlete.lastPurchaseId}
                    >
                      <i className="icon-undo" />
                      <span>{t('Mark as Unpaid')}</span>
                    </button>
                  )}
                  {canCancel && (
                    <button
                      onClick={() => setShowModal(true)}
                      className={dropdownStyles.dropdownItem}
                      disabled={loading}
                    >
                      <i className="icon-close" />
                      <span>{t('Cancel Subscription')}</span>
                    </button>
                  )}
                </div>
              </PaymentActionsDropdown>
            </div>
          ) : (
            <div className={dropdownStyles.actionMenuDisabled}>
              <i className="bullets" />
            </div>
          )}
        </div>

      {showModal && (
        <OfferingActionModal
          actions={{
            onCancel: handleCancel,
            onConfirm: handleConfirm,
          }}
          data={{
            title: t("Are you sure you want to cancel subscription?"),
            content: modalContent,
            button_text: t("Yes, cancel it"),
            operation: "delete",
          }}
          cancelButton={t("No, keep it")}
        />
      )}
    </div>
  );
}
